import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ViewChild, ElementRef, TemplateRef } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { CommunicationCenterService } from 'src/app/services/operations/communication-center.service';
import { CalltrackerService } from 'src/app/services/operations/calltracker.service';
import { GeneralService } from 'src/app/services/general.service';
import { ModalWrapperService } from 'src/app/services/modal-wrapper.service';
import { ICallCategory, ICallTag, ICommCallCategoryOrTagAssociation, ICommCallSummarySaved } from 'src/app/Shared/models/CommunicationCenter';
import { ICommCallSummary, ICommCallTranscript } from 'src/app/Shared/models/CommunicationCenter';
import { EUserFunctions } from 'src/app/Shared/enum';
import { BaseComponent } from 'src/app/Shared/base.component';
import { filter, take, takeUntil } from 'rxjs/operators';

// Same keys the Customer/Prospect Call Log's score input allows besides digits.
const SCORE_EDIT_KEYS = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Enter', 'Escape'];

interface TranscriptTurn {
  name: string;
  isAgent: boolean;
  time: string;
  text: string;
}

// Communication Center - Epic 2 Story 2.5/2.6/2.7: Call Summary detail - transcript/scoring/benchmarks (2.5),
// category/tag management (2.6), and Human Score override (2.7), all against one underlying PhoneCalls record.
// Category/tag/score edits are local until the panel's single Save (#128323), which sends them in one request.
@Component({
  selector: 'app-comm-call-summary',
  templateUrl: './comm-call-summary.component.html',
  styleUrls: ['./comm-call-summary.component.scss'],
  standalone: false
})
export class CommCallSummaryComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() phoneId!: number;
  // Optional row context (only the call log grid currently has these) - falls back to '-' when not passed in.
  @Input() customerName?: string;
  @Input() unitNumber?: string;
  @Input() unitLength?: number;
  @Input() unitWidth?: number;
  @Input() agentName?: string;
  @Input() durationSeconds?: number;
  @Output() closePanel = new EventEmitter<void>();
  // Emitted once the summary AND its recording/waveform (if any) have both finished loading - lets a
  // host page defer revealing/animating the panel until there's nothing left to load inside it.
  @Output() loaded = new EventEmitter<void>();
  // Emitted after a successful Save (just before closePanel) so a host grid can update that one row in place.
  @Output() saved = new EventEmitter<ICommCallSummarySaved>();
  @ViewChild('audioPlayer') audioPlayerRef?: ElementRef<HTMLAudioElement>;
  @ViewChild('discardChangesModal', { static: true }) discardChangesModal: TemplateRef<any>;
  private discardModalRef?: BsModalRef;

  activeTab: 'scoring' | 'transcript' = 'scoring';

  summary: ICommCallSummary | null = null;
  loading = false;
  // Loaded the first time the Transcript tab opens, and again after the panel switches to another call.
  transcriptTurns: TranscriptTurn[] = [];
  transcriptLoaded = false;
  transcriptLoading = false;
  transcriptUnavailable = false;
  // The Call Log page shows its own full-screen loader (gated on this component's `loaded` output) until
  // the very first load completes, so this component's own loader would otherwise double up with it during
  // that initial fetch - only shown here for loads that happen after that first one.
  private hasLoadedOnce = false;
  private permissionsReady = false;

  audioUrl: string | null = null;
  audioLoading = false;
  audioUnavailable = false;
  isPlaying = false;
  audioCurrentTime = 0;
  audioDuration = 0;
  waveformBars: number[] = [];

  callCategories: ICallCategory[] = [];
  callTags: ICallTag[] = [];
  selectedCategoryId: number | null = null;
  selectedTagId: number | null = null;

  humanScoreInput: number | null = null;
  // Override card (spec image-137) stays collapsed behind the "Override human score" button until clicked.
  showHumanScoreOverride = false;
  // A refused key/paste shows the range alert until the next accepted keystroke or the input loses focus.
  scoreBlocked = false;
  saving = false;

  // Last saved state - Cancel/X compare against it to decide whether to ask before discarding.
  private savedCategories: ICommCallCategoryOrTagAssociation[] = [];
  private savedTags: ICommCallCategoryOrTagAssociation[] = [];
  private savedHumanScore: number | null = null;

  // Only Save needs Communication Center Edit - without it edits stay local and are discarded on Cancel.
  editRight = false;
  viewRight = false;
  viewRightFalseMessage: string;

  constructor(public readonly communicationCenterService: CommunicationCenterService,
              private calltrackerService: CalltrackerService,
              private generalService: GeneralService,
              private modalWrapper: ModalWrapperService,
              private toastr: ToastrService) { super(); }

  ngOnInit(): void {
    if (this.generalService.UserPermissions?.Functions) {
      this.initialize();
    } else {
      this.generalService.userGroupPermissions.pipe(
        filter(() => !!this.generalService.UserPermissions?.Functions),
        take(1),
        takeUntil(this.unsubscribeOnDestroy$)
      ).subscribe(() => this.initialize());
    }
  }

  private initialize(): void {
    this.setUserPermissions();
    this.permissionsReady = true;
    if (this.phoneId) {
      this.load();
    }
    if (!this.viewRight) {
      return;
    }
    this.communicationCenterService.getCallCategories().subscribe({
      next: (categories) => this.callCategories = categories,
      error: () => this.toastr.error('Failed to load call categories.', 'Error')
    });
    this.communicationCenterService.getCallTags().subscribe({
      next: (tags) => this.callTags = tags,
      error: () => this.toastr.error('Failed to load call tags.', 'Error')
    });
  }

  private setUserPermissions(): void {
    const commCenterRights = this.generalService.getUserPermissionsForModule(EUserFunctions['Communication Center']);
    const callRecordingRights = this.generalService.getUserPermissionsForModule(EUserFunctions['Customer Call Recordings']);
    this.editRight = commCenterRights.EditRight;
    this.viewRight = commCenterRights.ViewRight || callRecordingRights.ViewRight;
    this.viewRightFalseMessage = this.generalService.ViewRightFalseMessage;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.phoneId && this.phoneId) {
      this.activeTab = 'scoring';
      this.load();
    }
  }

  load(): void {
    if (!this.permissionsReady || !this.phoneId) {
      return;
    }
    this.setUserPermissions();
    if (!this.viewRight) {
      this.summary = null;
      this.audioUrl = null;
      this.loading = false;
      this.audioLoading = false;
      this.hasLoadedOnce = true;
      this.toastr.warning(this.viewRightFalseMessage, 'Alert');
      this.loaded.emit();
      return;
    }
    this.loading = true;
    this.showHumanScoreOverride = false;
    this.audioUrl = null;
    this.audioUnavailable = false;
    this.isPlaying = false;
    this.audioCurrentTime = 0;
    this.audioDuration = 0;
    this.transcriptTurns = [];
    this.transcriptLoaded = false;
    this.transcriptLoading = false;
    this.transcriptUnavailable = false;

    this.communicationCenterService.getCallSummary(this.phoneId).subscribe({
      next: (summary) => {
        this.summary = summary;
        this.humanScoreInput = summary.HumanScore ?? null;
        this.scoreBlocked = false;
        this.snapshotSavedState();
        this.loading = false;

        if (!summary.DataExpired) {
          this.loadRecording();
        } else {
          this.hasLoadedOnce = true;
          this.loaded.emit();
        }
      },
      error: () => {
        this.loading = false;
        this.hasLoadedOnce = true;
        this.loaded.emit();
      }
    });
  }

  private loadRecording(): void {
    const summary = this.summary;
    const callRecordingId = summary?.CallRecordingId;
    const phoneSystem = summary?.PhoneSystem;
    if (!summary || !callRecordingId || !phoneSystem) {
      this.audioUnavailable = true;
      this.hasLoadedOnce = true;
      this.loaded.emit();
      return;
    }

    this.audioLoading = true;
    this.calltrackerService.getMediaPlayerURL(
      summary.PhoneNumber,
      phoneSystem,
      callRecordingId,
      String(summary.CallCenterIntegrationId ?? 0)
    ).subscribe({
      next: (url) => {
        this.audioUrl = url || null;
        this.audioUnavailable = !url;
        this.audioLoading = false;
        if (this.audioUrl) {
          this.loadWaveform(this.audioUrl);
        }
        this.hasLoadedOnce = true;
        this.loaded.emit();
      },
      error: () => {
        this.audioUnavailable = true;
        this.audioLoading = false;
        this.hasLoadedOnce = true;
        this.loaded.emit();
      }
    });
  }

  // Renders a real waveform (like the reference design) instead of a flat progress bar - decoded
  // client-side via Web Audio API. Falls back to a flat bar (see template) if decoding fails for any reason.
  private loadWaveform(url: string): void {
    this.waveformBars = [];
    const AudioContextCtor = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextCtor) {
      return;
    }

    fetch(url)
      .then(res => res.arrayBuffer())
      .then(buffer => new AudioContextCtor().decodeAudioData(buffer))
      .then((audioBuffer: AudioBuffer) => {
        const rawData = audioBuffer.getChannelData(0);
        const barCount = 60;
        const blockSize = Math.floor(rawData.length / barCount) || 1;
        const bars: number[] = [];
        for (let i = 0; i < barCount; i++) {
          let sum = 0;
          for (let j = 0; j < blockSize; j++) {
            sum += Math.abs(rawData[i * blockSize + j] || 0);
          }
          bars.push(sum / blockSize);
        }
        const max = Math.max(...bars, 0.0001);
        this.waveformBars = bars.map(v => Math.max(0.15, v / max));
      })
      .catch(() => {
        this.waveformBars = [];
      });
  }

  openTranscriptTab(): void {
    this.activeTab = 'transcript';
    if (!this.summary || this.summary.TranscriptUnavailable || this.transcriptLoaded || this.transcriptLoading) {
      return;
    }
    const phoneId = this.phoneId;
    this.transcriptLoading = true;
    this.communicationCenterService.getCallTranscript(phoneId).pipe(takeUntil(this.unsubscribeOnDestroy$)).subscribe({
      next: (transcript) => {
        if (phoneId !== this.phoneId) {
          return;
        }
        this.transcriptTurns = this.buildTranscriptTurns(transcript);
        this.transcriptLoaded = true;
        this.transcriptLoading = false;
      },
      error: () => {
        if (phoneId !== this.phoneId) {
          return;
        }
        this.transcriptUnavailable = true;
        this.transcriptLoading = false;
      }
    });
  }

  // Agent = the party with a RingCentral extension. Names prefer the SSM user / customer on the row, then RingCentral's.
  private buildTranscriptTurns(transcript: ICommCallTranscript): TranscriptTurn[] {
    const unnamedSpeakers: string[] = [];
    return transcript.Lines.map(line => {
      const speaker = transcript.Speakers.find(s => s.SpeakerId === line.SpeakerId);
      const isAgent = !!speaker?.ExtensionId;
      let name = speaker ? (isAgent ? this.agentName : this.customerName) || speaker.Name : '';
      if (!name) {
        // Transcripts saved before speakers were captured have no speaker list - number the parties by first appearance.
        if (!unnamedSpeakers.includes(line.SpeakerId)) {
          unnamedSpeakers.push(line.SpeakerId);
        }
        name = `Speaker ${unnamedSpeakers.indexOf(line.SpeakerId) + 1}`;
      }
      return { name, isAgent, time: this.formatAudioTime(line.Start), text: line.Text };
    });
  }

  trackByIndex(index: number): number {
    return index;
  }

  // FR-12: only admin-managed categories are selectable - no freeform entry. Local until Save.
  addCategory(): void {
    const summary = this.summary;
    const category = this.callCategories.find(c => c.Id === this.selectedCategoryId);
    if (!summary || !category) {
      return;
    }
    if (!summary.CallCategories.some(c => c.Id === category.Id)) {
      summary.CallCategories = [...summary.CallCategories, { Id: category.Id, Name: category.Name }];
    }
    this.selectedCategoryId = null;
  }

  removeCategory(categoryId: number): void {
    if (this.summary) {
      this.summary.CallCategories = this.summary.CallCategories.filter(c => c.Id !== categoryId);
    }
  }

  // FR-13: same behavior as categories, drawn from the admin-managed tag list.
  addTag(): void {
    const summary = this.summary;
    const tag = this.callTags.find(t => t.Id === this.selectedTagId);
    if (!summary || !tag) {
      return;
    }
    if (!summary.CallTags.some(t => t.Id === tag.Id)) {
      summary.CallTags = [...summary.CallTags, { Id: tag.Id, Name: tag.Name }];
    }
    this.selectedTagId = null;
  }

  removeTag(tagId: number): void {
    if (this.summary) {
      this.summary.CallTags = this.summary.CallTags.filter(t => t.Id !== tagId);
    }
  }

  openHumanScoreOverride(): void {
    this.showHumanScoreOverride = true;
  }

  // Same input rules as the Customer/Prospect Call Log score: digits only - letters, '-', '+', '.', 'e' are refused and
  // raise the range alert; editing keys and shortcuts (except paste) still work.
  onScoreKeydown(event: KeyboardEvent): void {
    const isShortcut = event.ctrlKey || event.metaKey;
    if (isShortcut && event.key.toLowerCase() !== 'v') {
      return;
    }
    if (/^\d$/.test(event.key) || (!isShortcut && SCORE_EDIT_KEYS.includes(event.key))) {
      if (event.key !== 'Tab') {
        this.scoreBlocked = false;
      }
      return;
    }
    event.preventDefault();
    this.scoreBlocked = true;
  }

  // Keeps the score within 100 by dropping trailing digits as they're typed (e.g. 888 -> 88).
  onScoreInput(input: HTMLInputElement): void {
    let digits = input.value;
    while (digits.length > 0 && Number(digits) > 100) {
      digits = digits.slice(0, -1);
    }
    if (digits !== input.value) {
      input.value = digits;
      this.humanScoreInput = digits === '' ? null : Number(digits);
    }
  }

  // No pasting or dropping text into the score - it has to be typed.
  blockScorePaste(event: Event): void {
    event.preventDefault();
    this.scoreBlocked = true;
  }

  // The score isn't mandatory, so leaving the input just drops the alert - it never holds up Save.
  onScoreBlur(): void {
    this.scoreBlocked = false;
  }

  get isDirty(): boolean {
    const summary = this.summary;
    if (!summary) {
      return false;
    }
    return !this.sameIds(summary.CallCategories, this.savedCategories)
      || !this.sameIds(summary.CallTags, this.savedTags)
      || this.humanScoreInput !== this.savedHumanScore;
  }

  // FR-14: setting a Human Score moves Review Status to Reviewed; clearing it (empty input) reverts to Not Reviewed.
  save(): void {
    const summary = this.summary;
    // Save stays greyed out until a category, tag or Human Score actually changes.
    if (!this.editRight || !summary || this.saving || !this.isDirty) {
      return;
    }

    this.saving = true;
    this.communicationCenterService.saveCallSummary({
      PhoneId: this.phoneId,
      CallCategoryIds: summary.CallCategories.map(c => c.Id),
      CallTagIds: summary.CallTags.map(t => t.Id),
      HumanScore: this.humanScoreInput
    }).pipe(takeUntil(this.unsubscribeOnDestroy$)).subscribe({
      next: (result) => {
        this.saving = false;
        const partErrors = [result.CategoryError, result.TagError, result.HumanScoreError].filter(Boolean);
        if (!result.Success && partErrors.length === 0) {
          this.toastr.error(result.Message || 'Failed to save Call Summary.', 'Error');
          return;
        }

        // Each part saves on its own: a rejected part goes back to its saved value, the rest become the new saved state.
        if (result.CategoryError) {
          summary.CallCategories = [...this.savedCategories];
        }
        if (result.TagError) {
          summary.CallTags = [...this.savedTags];
        }
        if (result.HumanScoreError) {
          this.humanScoreInput = this.savedHumanScore;
        }
        summary.HumanScore = this.humanScoreInput ?? undefined;
        summary.Variance = summary.HumanScore != null && summary.AiScore != null ? summary.HumanScore - summary.AiScore : undefined;
        this.snapshotSavedState();
        this.saved.emit({
          PhoneId: this.phoneId,
          CategoryNames: summary.CallCategories.map(c => c.Name),
          TagNames: summary.CallTags.map(t => t.Name),
          HumanScore: summary.HumanScore,
          Variance: summary.Variance
        });

        if (partErrors.length) {
          partErrors.forEach(message => this.toastr.error(message, 'Error'));
          return;
        }
        this.toastr.success('Call Summary saved successfully.', 'Success');
        this.closePanel.emit();
      },
      error: () => {
        this.saving = false;
        this.toastr.error('Failed to save Call Summary.', 'Error');
      }
    });
  }

  // Cancel and the header X: unsaved category/tag/score edits ask first (same Confirm popup as Payment Settings).
  requestClose(): void {
    if (this.isDirty) {
      this.discardModalRef = this.modalWrapper.openTemplate(this.discardChangesModal,
        { class: 'modal-dialog-centered comm-call-summary-discard', backdrop: 'static', keyboard: false });
      return;
    }
    this.closePanel.emit();
  }

  keepEditing(): void {
    this.discardModalRef?.hide();
  }

  discardChanges(): void {
    this.discardModalRef?.hide();
    const summary = this.summary;
    if (summary) {
      summary.CallCategories = [...this.savedCategories];
      summary.CallTags = [...this.savedTags];
    }
    this.humanScoreInput = this.savedHumanScore;
    this.scoreBlocked = false;
    this.showHumanScoreOverride = false;
    this.closePanel.emit();
  }

  private snapshotSavedState(): void {
    this.savedCategories = [...(this.summary?.CallCategories ?? [])];
    this.savedTags = [...(this.summary?.CallTags ?? [])];
    this.savedHumanScore = this.summary?.HumanScore ?? null;
  }

  private sameIds(current: ICommCallCategoryOrTagAssociation[], saved: ICommCallCategoryOrTagAssociation[]): boolean {
    return current.length === saved.length && current.every(item => saved.some(s => s.Id === item.Id));
  }

  get availableCategories(): ICallCategory[] {
    const usedIds = this.summary?.CallCategories?.map(c => c.Id) ?? [];
    return this.callCategories.filter(c => !usedIds.includes(c.Id));
  }

  get availableTags(): ICallTag[] {
    const usedIds = this.summary?.CallTags?.map(t => t.Id) ?? [];
    return this.callTags.filter(t => !usedIds.includes(t.Id));
  }

  formatDuration(totalSeconds?: number): string {
    if (totalSeconds == null) {
      return '-';
    }
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  varianceClass(variance?: number): string {
    if (variance == null) {
      return '';
    }
    return variance > 0 ? 'variance-pos' : variance < 0 ? 'variance-neg' : 'variance-zero';
  }

  get unitDimensions(): string | null {
    if (this.unitLength == null || this.unitWidth == null) {
      return null;
    }
    return `${this.unitLength}x${this.unitWidth}`;
  }

  get isBusy(): boolean {
    // Gated on hasLoadedOnce so this doesn't double up with the Call Log page's own loader, which already
    // covers the entire initial load (until the first `loaded` emit) - this one only fires for later loads.
    return this.hasLoadedOnce && (this.loading || this.audioLoading);
  }

  togglePlay(): void {
    const audio = this.audioPlayerRef?.nativeElement;
    if (!audio) {
      return;
    }
    if (audio.paused) {
      audio.play();
    } else {
      audio.pause();
    }
  }

  onAudioPlay(): void {
    this.isPlaying = true;
  }

  onAudioPause(): void {
    this.isPlaying = false;
  }

  onLoadedMetadata(): void {
    this.audioDuration = this.audioPlayerRef?.nativeElement.duration || 0;
  }

  onTimeUpdate(): void {
    this.audioCurrentTime = this.audioPlayerRef?.nativeElement.currentTime || 0;
  }

  seekAudio(event: MouseEvent, track: HTMLElement): void {
    const audio = this.audioPlayerRef?.nativeElement;
    if (!audio || !this.audioDuration) {
      return;
    }
    const rect = track.getBoundingClientRect();
    const ratio = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
    audio.currentTime = ratio * this.audioDuration;
  }

  get audioProgressPercent(): number {
    return this.audioDuration ? (this.audioCurrentTime / this.audioDuration) * 100 : 0;
  }

  formatAudioTime(totalSeconds: number): string {
    if (!totalSeconds || isNaN(totalSeconds)) {
      return '0:00';
    }
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}
